import { useEffect, useMemo, useRef, useState } from 'react';
import { isNativeShell } from '@/lib/shell';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import billingCopy from '@/i18n/ns/billing';
import { useOptionalWorkspace } from '@/workspace/context';
import { useIntlLocale } from '@/workspace/intl-locale';
import {
  applyCode,
  BillingActionError,
  changePlan,
  checkCode,
  fetchUsageOverview,
  SURFACES,
  type BillingStatus,
  type CodeCheck,
  type PlanChangeResult,
  type PlanTiming,
} from './api';
import { useBilling } from './context';
import { BillingDraftError } from './draft';
import { initialPlanIndex, percentText, shortDate, sortPlans } from './plan-math';
import { planStepView, timingView, type BillingCopy } from './plan-view';
import {
  CodeField,
  codeRefusal,
  DialogHead,
  PlanSlider,
  SidePanel,
  TimingChoices,
  type CodeFieldState,
  type CodeRefusal,
} from './change-plan-parts';

export interface ChangePlanDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  status: BillingStatus;
  /** Called after a plan change, a scheduled change or an applied code; refresh data here. */
  onChanged?: (result: PlanChangeResult) => void;
  /** Saves the composer draft before Stripe Checkout; defaults to the billing context's. */
  beforeRedirect?: () => void;
}

/** Plans are bought and changed on the web only (App Store rules); native shells never show this. */
export function ChangePlanDialog(props: ChangePlanDialogProps) {
  if (!props.open || isNativeShell()) return null;
  return <PlanDialog {...props} />;
}

const errorText = (failure: unknown, copy: BillingCopy) => {
  if (failure instanceof BillingDraftError)
    return failure.reason === 'attachments' ? copy.errorAttachments : copy.errorStorage;
  if (failure instanceof Error && failure.message === 'billing_edit_active')
    return copy.errorEditing;
  if (failure instanceof BillingActionError) {
    if (failure.code === 'card_declined') return copy.errorDeclined;
    if (failure.code === 'discount_active') return copy.errorDiscountActive;
    if (failure.code === 'complimentary_plan') return copy.errorComplimentary;
    if (failure.code === 'payment_failed') return copy.paymentFailed;
    if (failure.code === 'code_not_accepted') return copy.errorCodeNotAccepted;
    if (failure.code === 'code_already_used') return copy.codeAlreadyUsed;
    if (failure.code === 'code_not_percent') return copy.codeNotPercent;
  }
  return copy.errorGeneric;
};

/** Credits the company used in the last 30 days, for the caption under the plan. */
function useRecentCredits(): number | null {
  const [recent, setRecent] = useState<number | null>(null);
  useEffect(() => {
    const controller = new AbortController();
    fetchUsageOverview({}, controller.signal)
      .then((overview) => {
        const micros = overview.days.reduce(
          (sum, day) => sum + SURFACES.reduce((total, surface) => total + (day[surface] ?? 0), 0),
          0,
        );
        setRecent(Math.round(micros / 1_000_000));
      })
      .catch(() => {});
    return () => controller.abort();
  }, []);
  return recent;
}

/** Codes are typed loosely; spaces never belong to one. */
const cleanCode = (value: string) =>
  [...value.toUpperCase()]
    .filter((character) => character.trim() !== '')
    .join('')
    .slice(0, 24);

function useCodeField(): CodeFieldState & { fresh: CodeCheck | null; reset: () => void } {
  const [editing, setEditing] = useState(false);
  const [draft, setDraftValue] = useState('');
  const [error, setError] = useState(false);
  const [refusal, setRefusal] = useState<CodeRefusal>('not_found');
  const [busy, setBusy] = useState(false);
  const [fresh, setFresh] = useState<CodeCheck | null>(null);
  const reset = () => {
    setEditing(false);
    setDraftValue('');
    setError(false);
    setBusy(false);
  };
  return {
    editing,
    draft,
    error,
    refusal,
    busy,
    fresh,
    reset,
    open: () => {
      setEditing(true);
      setError(false);
    },
    cancel: reset,
    setDraft: (value) => {
      setDraftValue(cleanCode(value));
      setError(false);
    },
    apply: () => {
      const code = draft.trim();
      if (!code || busy) return;
      setBusy(true);
      setError(false);
      checkCode(code)
        .then((result) => {
          setFresh(result);
          reset();
        })
        .catch((failure) => {
          setBusy(false);
          setRefusal(codeRefusal(failure instanceof BillingActionError ? failure.code : null));
          setError(true);
        });
    },
    remove: () => setFresh(null),
  };
}

function PlanDialog({ onOpenChange, status, onChanged, beforeRedirect }: ChangePlanDialogProps) {
  const copy = useNamespaceCopy(billingCopy);
  const locale = useIntlLocale();
  const billing = useBilling();
  const notify = useOptionalWorkspace()?.notify;
  const plans = useMemo(() => sortPlans(status.plans), [status.plans]);
  const recentCredits = useRecentCredits();
  const code = useCodeField();
  const [chosen, setChosen] = useState<number | null>(null);
  const [step, setStep] = useState<'plans' | 'timing'>('plans');
  const [timing, setTiming] = useState<PlanTiming>('now');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dialog = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const node = dialog.current;
    if (node && !node.open) node.showModal?.();
  }, []);
  const exhausted = status.blockedReason === 'credits_exhausted';
  const index = chosen ?? initialPlanIndex(plans, status.account, recentCredits, exhausted);
  const inputs = { status, plans, index, fresh: code.fresh, recentCredits, locale, copy };
  const view = planStepView(inputs);
  const close = () => {
    if (!busy) onOpenChange(false);
  };
  const finish = (result: PlanChangeResult, message: string) => {
    notify?.(message, null, 'success');
    onChanged?.(result);
    void billing?.refresh();
    onOpenChange(false);
  };
  const run = async (task: () => Promise<void>) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await task();
    } catch (failure) {
      setError(errorText(failure, copy));
      setBusy(false);
    }
  };
  const checkout = () =>
    run(async () => {
      const save = beforeRedirect ?? billing?.beforeRedirect;
      if (billing?.hasActiveEdit()) throw new Error('billing_edit_active');
      save?.();
      const result = await changePlan({
        planId: view.plan.id,
        timing: 'now',
        code: code.fresh?.code,
      });
      if (result.kind !== 'checkout')
        return finish(result, formatCopy(copy.toastSwitched, { credits: view.credits }));
      save?.();
      location.assign(result.url);
    });
  const useCode = () =>
    run(async () => {
      const fresh = code.fresh;
      if (!fresh) return;
      const result = await applyCode(fresh.code);
      const date = status.account.periodEnd ? shortDate(status.account.periodEnd, locale) : '';
      const template =
        fresh.months == null
          ? copy.toastCodeForGood
          : fresh.months === 1
            ? copy.toastCodeOneMonth
            : copy.toastCode;
      finish(
        result,
        formatCopy(template, {
          percent: percentText(fresh.percentOff, locale),
          date,
          months: fresh.months ?? '',
        }),
      );
    });
  const confirm = () =>
    run(async () => {
      const result = await changePlan({
        planId: view.plan.id,
        timing,
        code: code.fresh?.code,
      });
      const date = status.account.periodEnd ? shortDate(status.account.periodEnd, locale) : '';
      finish(
        result,
        result.kind === 'scheduled'
          ? formatCopy(copy.toastScheduled, { date })
          : formatCopy(copy.toastSwitched, { credits: view.credits }),
      );
    });
  const cta = !view.hasPlan
    ? { label: busy ? copy.ctaOpening : copy.ctaCheckout, disabled: busy, action: checkout }
    : view.isCurrent
      ? {
          label: busy ? copy.ctaSaving : code.fresh ? copy.ctaUseCode : copy.ctaCurrent,
          disabled: busy || !code.fresh,
          action: useCode,
        }
      : {
          label: formatCopy(copy.ctaSwitch, { credits: view.credits }),
          disabled: busy,
          action: () => {
            setError(null);
            setTiming(index >= view.current ? 'now' : 'renewal');
            setStep('timing');
          },
        };
  return (
    <dialog
      ref={dialog}
      className="cp-root"
      aria-labelledby="cp-title"
      data-testid="change-plan-dialog"
      onCancel={(event) => {
        event.preventDefault();
        close();
      }}
    >
      <div
        className="cp-overlay-inner"
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) close();
        }}
      >
        <div className="cp-dialog">
          {step === 'plans' ? (
            <>
              <DialogHead
                title={view.hasPlan ? copy.dialogTitle : copy.dialogTitleNew}
                sub={copy.dialogSub}
                copy={copy}
                onClose={close}
              />
              <div className="cp-body">
                <div className="cp-main">
                  <div className="cp-kicker">
                    {view.kicker}
                    {view.isCurrent && <span className="cp-tag">{copy.currentPlanTag}</span>}
                  </div>
                  <div className="cp-hero">
                    <span className="cp-hero-num">{view.credits}</span>
                    <span className="cp-hero-unit">{copy.creditsEveryMonth}</span>
                  </div>
                  {view.caption && <p className="cp-caption">{view.caption}</p>}
                  <PlanSlider
                    plans={plans}
                    index={index}
                    current={view.current}
                    view={view}
                    copy={copy}
                    locale={locale}
                    onChange={(next) => {
                      setChosen(next);
                      setError(null);
                    }}
                  />
                </div>
                <SidePanel view={view} copy={copy}>
                  <CodeField code={code} view={view} copy={copy} locale={locale} />
                  {error && (
                    <p className="cp-alert" role="alert">
                      {error}
                    </p>
                  )}
                  <button
                    type="button"
                    className="cp-btn cp-cta"
                    data-primary
                    data-large
                    disabled={cta.disabled}
                    onClick={() => void cta.action()}
                  >
                    {cta.label}
                  </button>
                  <div className="cp-side-foot">{copy.sideFoot}</div>
                </SidePanel>
              </div>
            </>
          ) : (
            <TimingStep
              copy={copy}
              inputs={{ ...inputs, timing }}
              timing={timing}
              busy={busy}
              error={error}
              onTiming={setTiming}
              onBack={() => {
                setError(null);
                setStep('plans');
              }}
              onClose={close}
              onConfirm={() => void confirm()}
            />
          )}
        </div>
      </div>
    </dialog>
  );
}

function TimingStep({
  copy,
  inputs,
  timing,
  busy,
  error,
  onTiming,
  onBack,
  onClose,
  onConfirm,
}: {
  copy: BillingCopy;
  inputs: Parameters<typeof timingView>[0];
  timing: PlanTiming;
  busy: boolean;
  error: string | null;
  onTiming: (timing: PlanTiming) => void;
  onBack: () => void;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const view = timingView(inputs);
  return (
    <>
      <DialogHead title={copy.timingTitle} copy={copy} onClose={onClose} onBack={onBack} />
      <div className="cp-timing">
        <div className="cp-summary">
          <span className="cp-muted">{view.summaryFrom}</span>
          <span className="cp-muted" aria-hidden="true">
            →
          </span>
          <strong>{view.summaryTo}</strong>
          <span className="cp-muted" aria-hidden="true">
            ·
          </span>
          <span>{view.summaryPrice}</span>
        </div>
        <TimingChoices view={view} timing={timing} copy={copy} onPick={onTiming} />
        {error && (
          <p className="cp-alert" role="alert">
            {error}
          </p>
        )}
        <div className="cp-foot">
          <p className="cp-foot-note">{view.foot}</p>
          <div className="cp-foot-actions">
            <button type="button" className="cp-btn" data-large onClick={onClose}>
              {copy.cancel}
            </button>
            <button
              type="button"
              className="cp-btn"
              data-primary
              data-large
              disabled={busy}
              onClick={onConfirm}
            >
              {busy ? (timing === 'now' ? copy.paying : copy.scheduling) : view.confirm}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
